import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Gives control over expand panel interactions, possible values:
 * - 'none': No expand panel functionality, ExpandPanelContext is undefined.
 * - 'tooltip': Tooltip will trigger expand / focus item after clicking to pin.
 * - 'disabled': Explicitly disable expand, allows for nesting providers (ex: disable expand when already in a dialog).
 */
export type ExpandPanelMode = 'none' | 'tooltip' | 'disabled';

interface ExpandPanelState {
  mode: ExpandPanelMode;
  expandedPanelKey: string | undefined;
  setExpandedPanelKey: (expandedPanelKey: string | undefined) => void;
  focusedItemKey: string | undefined;
  setFocusedItemKey: (focusedItemKey: string | undefined) => void;
}

export const defaultExpandPanelState: ExpandPanelState = {
  mode: 'none',
  expandedPanelKey: undefined,
  setExpandedPanelKey: () => undefined,
  focusedItemKey: undefined,
  setFocusedItemKey: () => undefined,
};

interface ExpandPanelProviderProps {
  mode?: ExpandPanelMode;
  children?: React.ReactNode;
}

/**
 * Manages state for the expanded panel fullscreen view. Tracks which panel is expanded
 * and optionally which item within it is focused (e.g. series, cell, datum).
 * Can be triggered from chart interactions with pinned tooltip, legend items, or context menu.
 */
export function ExpandPanelProvider(props: ExpandPanelProviderProps) {
  const { children, mode = 'none' } = props;

  if (mode === 'none') {
    return <>{children}</>;
  }

  return <ExpandPanelProviderWithMode mode={mode}>{children}</ExpandPanelProviderWithMode>;
}

interface ExpandPanelProviderWithModeProps {
  mode?: ExpandPanelMode;
  children?: React.ReactNode;
}

/**
 * Use mode option to set default interaction pattern for expanding a panel with optional item focus.
 */
function ExpandPanelProviderWithMode(props: ExpandPanelProviderWithModeProps) {
  const { children, mode = 'none' } = props;

  // Allow nesting expand panel providers with different modes (ex: to disable expand action in dialog)
  const parentCtx = useContext(ExpandPanelContext);
  const [expandedPanelKey, setExpandedPanelKey] = useState<string | undefined>(parentCtx?.expandedPanelKey);
  const [focusedItemKey, setFocusedItemKey] = useState<string | undefined>(parentCtx?.focusedItemKey);

  const ctx = useMemo(
    () => ({
      mode,
      expandedPanelKey,
      setExpandedPanelKey,
      focusedItemKey,
      setFocusedItemKey,
    }),
    [mode, expandedPanelKey, setExpandedPanelKey, focusedItemKey, setFocusedItemKey]
  );

  return <ExpandPanelContext.Provider value={ctx}>{children}</ExpandPanelContext.Provider>;
}

const ExpandPanelContext = createContext<ExpandPanelState | undefined>(undefined);

export function useExpandPanelContext(): ExpandPanelState {
  const ctx = useContext(ExpandPanelContext);
  // Ensures ExpandPanelProvider is optional since only used on dashboards currently
  return ctx ?? defaultExpandPanelState;
}

export function useExpandedPanelKey() {
  const ctx = useExpandPanelContext();
  return ctx.expandedPanelKey;
}

export function useFocusedItemKey() {
  const ctx = useExpandPanelContext();
  return ctx.focusedItemKey;
}

/*
 * Get focused item key for use in expand dialog / focus actions.
 */
export function useExpandPanelFocusedItemKey() {
  const { expandedPanelKey, focusedItemKey } = useExpandPanelContext();
  if (!expandedPanelKey || !focusedItemKey) return;
  // TODO: allow more generic behavior since this is coupled to how TimeSeriesChartPanel seriesId is used in legend
  return focusedItemKey;
}

/*
 * Set expand panel context state, can be used inside custom tooltip or leveraged in chart events.
 */
export const useUpdateExpandPanelFocus = (panelKey?: string) => {
  const { setExpandedPanelKey, setFocusedItemKey, mode } = useExpandPanelContext();

  const updateFocusedItem = useCallback(
    (itemKey: string) => {
      setExpandedPanelKey(panelKey);
      setFocusedItemKey(itemKey);
    },
    [panelKey, setFocusedItemKey, setExpandedPanelKey]
  );

  if (!panelKey || mode === 'none' || mode === 'disabled') {
    return;
  }

  return updateFocusedItem;
};
